import { ICalendarMeetingAttendee, ICalendarMeetingInfo } from '../../../viewmodels/CalendarViewModels';

const MEETING_TIME_ZONE = 'America/New_York';

const meetingDateFormatter = new Intl.DateTimeFormat('en-US', {
	day: 'numeric',
	month: 'short',
	timeZone: MEETING_TIME_ZONE,
	weekday: 'short',
});

const meetingTimeFormatter = new Intl.DateTimeFormat('en-US', {
	hour: 'numeric',
	minute: '2-digit',
	timeZone: MEETING_TIME_ZONE,
});

/**
 * Parses a timestamp from the calendar API, which returns UTC but omits the `Z` designator. Without
 * it `new Date` reads the value as browser-local, so the meeting renders at the wrong time for any
 * admin outside UTC.
 *
 * @param dateString The timestamp to parse.
 */
export function parseMeetingDate(dateString: string): Date {
	const hasZoneDesignator = /(?:Z|[+-]\d{2}:?\d{2})$/.test(dateString);
	return new Date(hasZoneDesignator ? dateString : `${dateString}Z`);
}

// Intl separates the meridiem with a narrow no-break space, which collapses to a plain space here so
// the rendered string and its assertions stay comparable.
function formatInTimeZone(formatter: Intl.DateTimeFormat, date: Date): string {
	return formatter.format(date).replace(/\s+/g, ' ');
}

function isValidDate(date: Date | null): date is Date {
	return !!date && !Number.isNaN(date.getTime());
}

/**
 * Picks the best available label for a meeting attendee, preferring the linked Levitate user's name
 * over the name the calendar provider supplied.
 *
 * @param attendee The attendee to label.
 */
export function getAttendeeDisplayName(attendee: ICalendarMeetingAttendee): string {
	return attendee.userName || attendee.name || attendee.email || 'Unknown';
}

/**
 * Formats a meeting's date, time range, and duration into a single context line in Eastern time,
 * matching how the meetings table renders the same values — e.g. `'Wed, Sep 10 · 3:00–3:45 PM · 45
 * min'`. The start meridiem is dropped only when the meeting starts and ends in the same half of the
 * same day; a meeting that runs past midnight repeats the date, so it cannot read as zero-length.
 * Segments whose source values are missing or unparseable are omitted, so a meeting with neither a
 * valid start time nor a duration yields an empty string.
 *
 * @param meeting The meeting to describe.
 */
export function formatMeetingSchedule(meeting: ICalendarMeetingInfo): string {
	const start = meeting.startTime ? parseMeetingDate(meeting.startTime) : null;
	const end = meeting.endTime ? parseMeetingDate(meeting.endTime) : null;
	const segments: string[] = [];

	if (isValidDate(start)) {
		const startDateText = formatInTimeZone(meetingDateFormatter, start);
		const startTimeText = formatInTimeZone(meetingTimeFormatter, start);
		segments.push(startDateText);

		if (isValidDate(end)) {
			const endDateText = formatInTimeZone(meetingDateFormatter, end);
			const endTimeText = formatInTimeZone(meetingTimeFormatter, end);

			if (endDateText === startDateText) {
				const [startClock, startMeridiem] = startTimeText.split(' ');
				const endMeridiem = endTimeText.split(' ')[1];
				segments.push(`${startMeridiem === endMeridiem ? startClock : startTimeText}–${endTimeText}`);
			} else {
				segments.push(`${startTimeText} – ${endDateText} ${endTimeText}`);
			}
		} else {
			segments.push(startTimeText);
		}
	}

	if (meeting.durationMinutes > 0) {
		segments.push(`${meeting.durationMinutes} min`);
	}

	return segments.join(' · ');
}
